import Card from "@/components/Card"
import InputError from "@/components/InputError"
import SkeletonRow from "@/components/SkeletonRow"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import useApiBaseUrl from "@/hooks/useApiBaseUrl"
import api from "@/lib/axios"
import { formatRupiah, getInitials } from "@/lib/utils"
import { DataSiswa } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaCheck, FaFaceSadTear, FaFaceSmileBeam } from "react-icons/fa6"
import { GiBackwardTime } from "react-icons/gi"
import { MdBlockFlipped } from "react-icons/md"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import LogoEganesha from "@/assets/logos/e-ganesha.png"
import { IoWarning } from "react-icons/io5"
import Modal from "@/components/Modal"
import { web } from "@/config/System"

type DataSiak = {
  id_data_pendaftaran: string
  nomor_pendaftaran: string
  jalur_jenis: string
  siswa: DataSiswa
  email_pribadi: string
  email_sso: string
  nim: string
  pilihan_prodi: string
  nominal_ukt: number
  status_loket_5: number | boolean | null
  keterangan_loket_5: string | null
}

type ValidasiState = {
  id_data_pendaftaran?: string
  keterangan_loket_5: string
  status_loket_5?: number
}

const getDataSiak = async (searchQuerySiswa: string) => {
  const response = await api.get(
    `/verifikator/daftar-kembali/loket-5/data-siak?id=${searchQuerySiswa}`
  )
  return response.data?.data
}

const patchDataValidasi = async (form: ValidasiState) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/loket-5/validasi",
    form
  )
  return response.data
}

const patchDataResetValidasi = async (id_data_pendaftaran: string) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/reset?loket=5",
    { id_data_pendaftaran }
  )
  return response.data
}

const getDataStatusGenerateAkun = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/verifikator/daftar-kembali/loket-5/status-generate-akun?id=${id_data_pendaftaran}`
  )
  return response.data
}

export default function Loket5({ disabled }: { disabled: boolean }) {
  const [searchParams] = useSearchParams()
  const idParams = searchParams.get("id")
  const { apiBaseUrl } = useApiBaseUrl()

  const { data, isPending, error } = useQuery<DataSiak, AxiosError>({
    queryKey: ["dataSiak", idParams],
    queryFn: () => getDataSiak(idParams || ""),
    enabled: !!idParams,
  })

  const [form, setForm] = useState<ValidasiState>({
    keterangan_loket_5: "",
  })
  const [showAlert, setShowAlert] = useState(false)

  const queryClient = useQueryClient()
  const {
    mutate,
    isPending: isPendingValidation,
    error: errorValidation,
  } = useMutation({
    mutationFn: patchDataValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataSiak", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateResetValidation,
    isPending: isPendingResetValidation,
    // error: errorResetValidation,
  } = useMutation({
    mutationFn: patchDataResetValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataSiak", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateGenerateAkun,
    isPending: isPendingGenerateAkun,
    error: errorGenerateAkun,
  } = useMutation({
    mutationFn: () => getDataStatusGenerateAkun(idParams || ""),
    onSuccess: (data) => {
      if (data?.ok) {
        window.open(`${apiBaseUrl}/siswa/daftar-kembali/generate-akun-eganesha`)
        toast("Memproses cetak nim.")
      }
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
      setShowAlert(true)
    },
  })

  const handleValidasi = (status: number) => {
    mutate({
      ...form,
      id_data_pendaftaran: data?.id_data_pendaftaran,
      status_loket_5: status,
    })
  }

  const handleReset = () => {
    mutateResetValidation(data?.id_data_pendaftaran || "")
  }

  const handleGenerateAkun = () => {
    mutateGenerateAkun()
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />
          })}
      </div>
    )
  }

  if (error?.response?.status === 404) {
    return (
      <div>
        Data <span className="text-primary">{idParams}</span> tidak ditemukan.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSmileBeam className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold">
            Lakukan pencarian menggunakan nomor pendaftaran atau nama siswa.
          </h1>
        </div>
      </div>
    )
  }

  if (disabled) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSadTear className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold text-center">
            Siswa atas nama {data?.siswa?.nama_lengkap}{" "}
            <span className="text-primary">
              belum divalidasi di loket sebelumnya
            </span>
          </h1>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex gap-3">
      <Avatar className="w-16 h-16">
        <AvatarImage src={data?.siswa?.path_foto || ""} alt={"foto"} />
        <AvatarFallback>
          {getInitials(`${data?.siswa?.nama_lengkap}`)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 overflow-auto">
        <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
          <tbody>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nomor Pendaftaran
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.nomor_pendaftaran}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nama Peserta
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa?.nama_lengkap}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Email Pribadi
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.email_pribadi}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nomor Handphone
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa?.handphone}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                NIM
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%] font-semibold">
                {data?.nim}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Email {web.univAcronym}
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%] font-semibold">
                {data?.email_sso}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Program Studi
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%] font-semibold">
                {data?.pilihan_prodi}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                UKT
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%] font-semibold">
                {data?.nominal_ukt && formatRupiah(data?.nominal_ukt)}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Status Validasi Loket
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.status_loket_5 === 1 ? (
                  <Badge variant={"dark"}>Sudah Valid</Badge>
                ) : data?.status_loket_5 === 0 ? (
                  <Badge variant={"outline"}>Tidak Valid</Badge>
                ) : (
                  <Badge variant={"secondary"}>Belum Valid</Badge>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Catatan
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.keterangan_loket_5}
              </td>
            </tr>
          </tbody>
        </table>

        {data?.status_loket_5 === null ? (
          <Card className="mt-3 px-4">
            <div>
              <Label>Input Catatan</Label>
              <Textarea
                placeholder="Ketik catatan..."
                className="mt-1"
                value={form.keterangan_loket_5}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    keterangan_loket_5: event.target.value,
                  }))
                }
              />
              <InputError
                text={errorValidation?.response?.data.errors?.keterangan_loket_5?.join(
                  ","
                )}
              />
            </div>

            <div className="mt-5 flex justify-end gap-3">
              {isPendingValidation ? (
                <Button
                  variant={"outline"}
                  className="flex items-center gap-3"
                  disabled
                >
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Processing...
                  </>
                </Button>
              ) : (
                <>
                  <Button
                    variant={"outline"}
                    className="flex items-center gap-3"
                    onClick={() => handleValidasi(0)}
                    disabled={isPendingValidation}
                  >
                    <MdBlockFlipped />
                    Tidak Valid
                  </Button>
                  <Button
                    className="flex items-center gap-3"
                    onClick={() => handleValidasi(1)}
                    disabled={isPendingValidation}
                  >
                    <FaCheck />
                    Valid
                  </Button>
                </>
              )}
            </div>
          </Card>
        ) : (
          <div className="mt-5 flex justify-end">
            <Button
              variant="outline"
              className="flex items-center gap-3"
              onClick={() => handleReset()}
              disabled={isPendingResetValidation}
            >
              {isPendingResetValidation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <GiBackwardTime />
                  Reset Hasil Validasi
                </>
              )}
            </Button>
          </div>
        )}

        <div className="mt-5">
          <Button
            variant={"outline"}
            onClick={handleGenerateAkun}
            className="w-full flex items-center gap-3"
            disabled={isPendingGenerateAkun}
          >
            {isPendingGenerateAkun ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <img
                  src={LogoEganesha}
                  alt="E-Ganesha"
                  width={20}
                  height={20}
                />
                Generate Akun E-Ganesha
              </>
            )}
          </Button>
        </div>
      </div>

      <Modal show={showAlert} onClose={() => setShowAlert(false)}>
        <div>
          <IoWarning size={50} className="mx-auto" />
          <h1 className="mt-3 mb-5 text-xl font-semibold text-center">
            Perhatian!
          </h1>
          <div className="p-3 mt-3 rounded-xl border border-yellow-500 bg-yellow-50">
            <h1>Mohon maaf, saat ini anda belum bisa melakukan cetak nim.</h1>
            <br />
            <p className="text-primary">
              {errorGenerateAkun?.response?.data?.message}
            </p>
            <br />
          </div>
          <div className="mt-5">
            <Button variant={"outline"} onClick={() => setShowAlert(false)}>
              Tutup
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
